import { Box, Tab, Tabs, Typography } from "@mui/material";
import {
  AddEditDocumentDialogState,
  EditMode,
  FormState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { WorkflowStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { DateTime } from "luxon";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useCustomWorkDetail } from "./useCustomWorkDetail";
import { WorkCommentsPanel } from "./WorkCommentsPanel";
import { WorkDetailDialogs } from "./WorkDetailDialogs";
import { WorkDetailHeader } from "./WorkDetailHeader";
import { WorkDetailError, WorkDetailLoading } from "./WorkDetailStates";
import { DetailTab, WorkDetailTabPanels } from "./WorkDetailTabPanels";
import { WorkStep } from "./WorkStepsList";

const TERMINAL_WORKFLOW_STATUSES: WorkflowStatus[] = [
  WorkflowStatus.Completed,
  WorkflowStatus.Canceled,
];

/**
 * Open state for the add-document dialog. With a step it attaches to that step
 * (the dialog creates the ActivityDocument itself); without one the caller
 * attaches at the workflow level.
 */
function addDocumentState(step?: WorkStep): AddEditDocumentDialogState {
  return {
    ...defaultAddEditDocumentDialogState,
    open: true,
    mode: EditMode.ADD,
    step: FormState.UPLOAD,
    ...(step ? { activityId: step.id } : {}),
  };
}

/**
 * The custom deliverable detail screen. A flat deliverable is a single staff
 * activity, rendered as an activity card (attachment, status line,
 * start/complete/skip/undo/assign). Documents and History get their own tabs; the
 * unified comment stream anchors the page for conversation about the
 * deliverable as a whole.
 */
export function CustomWorkDetail() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [tab, setTab] = useState<DetailTab>("steps");
  const [assignStepId, setAssignStepId] = useState<string | null>(null);
  // One dialog serves both attach flows: with an activityId it attaches to
  // that step (the dialog itself creates the ActivityDocument); without one
  // we attach at the workflow level from the onSubmit callback.
  const [documentDialog, setDocumentDialog] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  const {
    detail,
    loading,
    error,
    refetch,
    busy,
    addStep,
    startStep,
    completeStep,
    skipStep,
    reopenStep,
    attachDocument,
    removeDocument,
    updateAssignees,
    getDocumentDownloadLink,
  } = useCustomWorkDetail(workflowId);

  useBreadcrumb({ label: detail?.name ?? "Custom deliverable" });

  if (!workflowId) {
    return null;
  }
  if (loading && !detail) {
    return <WorkDetailLoading />;
  }
  if (error || !detail) {
    return <WorkDetailError error={error} />;
  }

  const canEdit = !TERMINAL_WORKFLOW_STATUSES.includes(detail.status);
  const due = detail.dueAt ? DateTime.fromISO(detail.dueAt) : null;
  const overdue = due !== null && due < DateTime.now() && canEdit;

  const openDocumentDialog = (step?: WorkStep) => setDocumentDialog(addDocumentState(step));

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, maxWidth: 1100 }}>
      <WorkDetailHeader
        workTypeLabel={detail.workTypeLabel}
        status={detail.status}
        tenantName={detail.tenantName}
        programArea={detail.programArea}
        due={due}
        dueType={detail.dueType}
        overdue={overdue}
      />

      {detail.description && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
          {detail.description}
        </Typography>
      )}

      <Tabs value={tab} onChange={(_, next: DetailTab) => setTab(next)}>
        <Tab label="Steps" value="steps" />
        <Tab label={`Documents (${detail.documents.length})`} value="documents" />
        <Tab label="History" value="history" />
      </Tabs>

      <WorkDetailTabPanels
        tab={tab}
        detail={detail}
        canEdit={canEdit}
        busy={busy}
        onStart={(activityId) => void startStep({ variables: { activityId } })}
        onComplete={(activityId) => void completeStep({ variables: { activityId } })}
        onSkip={(activityId) => void skipStep({ variables: { activityId } })}
        onAssign={setAssignStepId}
        onReopen={(activityId) => void reopenStep({ variables: { activityId } })}
        onAttachDocument={openDocumentDialog}
        onOpenDocument={(documentId) =>
          void getDocumentDownloadLink({ variables: { id: documentId } })
        }
        onDetachDocument={(documentId) =>
          void removeDocument({ variables: { workflowId, documentId } })
        }
        onAddStep={addStep}
      />

      <WorkCommentsPanel workflowId={workflowId} comments={detail.comments} />

      <WorkDetailDialogs
        tenantId={detail.tenantId}
        assignStepId={assignStepId}
        setAssignStepId={setAssignStepId}
        onAssign={async (activityId, userId) => {
          await updateAssignees({ variables: { ids: [activityId], assigneeId: userId } });
        }}
        documentDialog={documentDialog}
        setDocumentDialog={setDocumentDialog}
        onDocumentSubmit={(documentId, activityId) => {
          if (activityId) {
            // Step attach: the dialog already linked the document to the
            // activity — just pick up the new state.
            void refetch();
          } else {
            void attachDocument({ variables: { workflowId, documentId } });
          }
        }}
      />
    </Box>
  );
}
